// CHAT TYPES
export interface ChatState {
  sessions: ChatSession[];
  currentSession: ChatSession | null;
  models: AIModel[];
  selectedModel: string;
  isLoading: boolean;
  error: string | null;
  input: string;
  promptImage: string;
  lastPromptImage: string;
  setPromptImage: (promptImage: string) => void;
  setLastPromptImage: (lastPromptImage: string) => void;
  promptImageFile: string | null;
  setPromptImageFile: (promptImageFile: string | null) => void;
  currentPage: number;
  hasMoreMessages: boolean;
  isLoadingMore: boolean;
  folders: Folder[];
  loadSessions: () => Promise<void>;
  createSession: (sessionType: ChatSessionType) => Promise<any>;
  deleteSession: (id: string) => Promise<void>;
  loadSessionMessages: (sessionId: string) => Promise<void>;
  sendMessage: (
    content: string,
    temperature: number,
    memory: number
  ) => Promise<void>;
  sendMultiMessage: (
    content: string,
    models: string[],
    temperatures: number[],
    memories: number[]
  ) => Promise<void>;
  sendVideoMessage: (
    promptText: string,
    promptImage: string,
    promptLastImage: string,
    duration: 5 | 10
  ) => Promise<void>;
  sendPresentationMessage: (
    request: PresentationRequest
  ) => Promise<Blob | ArrayBuffer | undefined>;
  sendImageMessage: (request: IRequestImage) => Promise<void>;
  sendDecor8Message: (request: GenerateImageDecor8Request) => Promise<void>;
  sendDecor8PrimeWallsMessage: (
    request: Decor8PrimeWallsRequest
  ) => Promise<void>;
  sendAudioMessage: (request: GenerateTextToSpeechRequest) => Promise<void>;
  sendAgentMessage: (request: AgentMessageRequest) => Promise<void>;
  loadModels: () => Promise<void>;
  setSelectedModel: (modelId: string) => void;
  updateSessionName: (sessionId: string, name: string) => Promise<void>;
  updateSessionIndexes: (sessionId: string, indexes: number) => Promise<void>;
  setInput: (content: string) => void;
  loadOlderMessages: () => Promise<void>;
  loadFolders: () => Promise<void>;
  createFolder: (name: string) => Promise<void>;
  updateFolder: (id: string, name: string) => Promise<void>;
  deleteFolder: (id: string) => Promise<void>;
  addChatToFolder: (folderId: string, id: string) => Promise<void>;
  removeChatFromFolder: (id: string) => Promise<void>;
}
export interface ChatMessage {
  id: string;
  sessionId: string;
  content: string;
  role: "user" | "assistant" | "system";
  createdAt: string;
  model?: string;
  index?: number;
  presentationData?: ArrayBufferLike;
  timestamp?: string;
  // metadata?: Record<string, any>;
}
export enum ChatSessionType {
  STANDARD = "STANDARD",
  COMPARATIVE_CHAT = "COMPARATIVE_CHAT",
  IMAGE_CHAT = "IMAGE_CHAT",
  VIDEO_CHAT = "VIDEO_CHAT",
  PRESENTATION = "PRESENTATION",
  AUDIO_CHAT = "AUDIO_CHAT",
  MUSIC_CHAT = "MUSIC_CHAT",
  NUVEM_SHOP_CHAT = "NUVEM_SHOP_CHAT",
  DATA_MINING = "DATA_MINING",
  CHARACTER_CHAT = "CHARACTER_CHAT",
  AGENT_CHAT = "AGENT_CHAT",
}
export interface ChatSession {
  id: string;
  sessionName?: string;
  messages: ChatMessage[];
  createdAt: string;
  sessionType: ChatSessionType;
  indexes: number;
}

export interface GenerateTextToSpeechRequest {
  input: string;
  model: string;
  voice: string;
}

// AI TYPES
export interface AIModel {
  id: string;
  name: string;
  provider: string;
  type: "llm" | "generation" | "reranker";
  description?: string;
  description_ptBR?: string;
  capabilities?: string[];
  maxOutputTokens?: number;
  contextWindow?: number;
  outputDimension?: number;
  enabled: boolean;
  inputCostPer1M?: number;
  outputCostPer1M?: number;
  costPerGeneration?: number;
  costPer1MCharacters?: number;
}
export interface GenerateResponse {
  content: string;
  done?: boolean;
  kwargs?: {
    content: string;
  };
}

export interface GenerateVideoRequest {
  model: "gen3a_turbo";
  promptImage: { uri: string; position: "first" | "last" }[];
  promptText: string;
  duration: 5 | 10;
  watermark: boolean;
  messageId: string;
}

export interface GenerateVideoResponse {
  id: string;
  status:
    | "RUNNING"
    | "SUCCEEDED"
    | "FAILED"
    | "PENDING"
    | "CANCELLED"
    | "THROTTLED";
  createdAt: string;
  output?: string[];
}

// PRESENTATION TYPES
export type TemplateType = "modern" | "classic" | "minimal" | "gradient";
export interface PresentationRequest {
  sessionId: string;
  topic: string;
  numSlides?: number;
  model?: string;
  temperature?: number;
  language?: string;
  template?: TemplateType;
}
// AGENT TYPES
export interface AgentMessageRequest {
  prompt: string;
  agentId: string;
  sessionId: string;
}
// SYSTEM TYPES
export type Payment = {
  id: string;
  type: "PREPAID" | "SUBSCRIPTION" | "DEPOSIT_BONUS";
  status: "PENDING" | "PAID" | "FAILED" | "CANCELLED";
  amount: number;
  currency: string;
  description: string;
  createdAt: string;
  paymentUrl?: string;
  qrCodeImage?: string;
  pixCopiaECola?: string;
  paymentMethod?: string;
  user?: User;
};
export interface User {
  id?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  cpf?: string;
  birthday?: string;
  googleId?: string;
  githubId?: string;
  twitterId?: string;
  facebookId?: string;
  affiliateId?: string;
  isAdmin?: boolean;
  plan?: string;
  credits?: number;
  status?: string;
  billingPeriod?: string;
  nextBillingDate?: string;
  password?: string;
  createdAt?: string;
  updatedAt?: string;
  subscription?: Subscription;
  payments?: Payment[];
}
export interface Subscription {
  id: string;
  plan: string;
  credits: number;
  totalSpent: number;
  status: string;
  createdAt: string;
  updatedAt: string;
}
export enum TicketStatus {
  OPEN = "OPEN",
  IN_PROGRESS = "IN_PROGRESS",
  RESOLVED = "RESOLVED",
  CLOSED = "CLOSED",
}
export enum TicketPriority {
  LOW = "LOW",
  MEDIUM = "MEDIUM",
  HIGH = "HIGH",
  URGENT = "URGENT",
}
export enum FeatureStatus {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  PLANNED = "PLANNED",
  IN_PROGRESS = "IN_PROGRESS",
  PAUSED = "PAUSED",
  COMPLETED = "COMPLETED",
  // DECLINED = "DECLINED",
}
export interface Ticket {
  id: string;
  title: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  createdAt: string;
  updatedAt: string;
  messages: TicketMessage[];
  user: User;
}
export interface CreateTicketMessage {
  content: string;
}
export interface TicketMessage {
  id: string;
  content: string;
  ticketId: string;
  isStaffResponse: boolean;
  createdAt: string;
  updatedAt: string;
  user?: UserInfo;
}
export interface FeatureRequest {
  id: string;
  title: string;
  description: string;
  status: FeatureStatus;
  voteCount: number;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
  user: User;
  votes: FeatureVote[];
}

export interface CreateFeatureRequest {
  title: string;
  description: string;
}

export interface FeatureVote {
  id: string;
  featureRequestId: string;
  userId: string;
  createdAt: string;
  user: User;
}

export interface UserInfo {
  firstName?: string;
  lastName?: string;
  email: string;
}
interface Folder {
  id: string;
  name: string;
  chatSessions: {
    id: string;
    sessionName: string | null;
    sessionType: ChatSessionType;
    indexes: number;
  }[];
}
type IOutputType = "base64Data" | "dataURI" | "URL";
export type IOutputFormat = "JPEG" | "PNG" | "WEBP";

export type TModelSearch = {
  search?: string;
  tags?: string[];
  category?: "checkpoint" | "lora" | "controlnet";
  type?: string;
  architecture?: EModelArchitecture;
  conditioning?: string;
  visibility?: "public" | "private" | "all";
  limit?: number;
  offset?: number;
  customTaskUUID?: string;
  retry?: number;
} & {
  [key: string]: any;
};

/*
response:{
	"results": [
		{
			"name": "epiCRealism XL",
			"air": "civitai:277058@367058",
			"tags": [
				"base model",
				"photo",
				"woman",
				"photography",
				"photorealism",
				"realistic"
			],
			"heroImage": "https://mim.runware.ai/r/6638f3d1e2a63-450x788.jpg",
			"category": "checkpoint",
			"private": false,
			"comment": "",
			"version": "V4-PhotoReal",
			"architecture": "sdxl",
			"type": "base",
			"defaultWidth": 1024,
			"defaultHeight": 1024,
			"defaultSteps": 20,
			"defaultScheduler": "Default",
			"defaultCFG": 7.5
		},...
]...

*/

export type TModelSearchResponse = {
  results: TModelSearchResult[];
};

export type TModelSearchResult = {
  name: string;
  air: string;
  tags: string[];
  heroImage: string;
  category: string;
  private: boolean;
  version: string;
  architecture: string;
  type: string;
  defaultWidth: number;
  defaultHeight: number;
  defaultSteps: number;
  defaultScheduler: string;
  defaultCFG: number;
};

export enum EModelArchitecture {
  flux1d = "flux1d",
  flux1s = "flux1s",
  pony = "pony",
  sdhyper = "sdhyper",
  sd1x = "sd1x",
  sd1xlcm = "sd1xlcm",
  sd3 = "sd3",
  sdxl = "sdxl",
  sdxllcm = "sdxllcm",
  sdxldistilled = "sdxldistilled",
  sdxlhyper = "sdxlhyper",
  sdxllightning = "sdxllightning",
  sdxlturbo = "sdxlturbo",
}

export interface IRequestImage {
  outputType?: IOutputType;
  outputFormat?: IOutputFormat;
  uploadEndpoint?: string;
  checkNSFW?: boolean;
  positivePrompt: string;
  negativePrompt?: string;
  seedImage?: File | string;
  maskImage?: File | string;
  strength?: number;
  height?: number;
  width?: number;
  model: number | string;
  steps?: number;
  scheduler?: string;
  seed?: number;
  maskMargin?: number;
  CFGScale?: number;
  clipSkip?: number;
  numberResults?: number;
  includeCost?: boolean;
  customTaskUUID?: string;
  retry?: number;
}

export interface Decor8UpscaleRequest {
  imageUrl: string;
  scaleFactor: number;
}

export interface Decor8PrimeWallsRequest {
  sessionId: string;
  imageUrl: string;
}

export interface GenerateImageDecor8Request {
  sessionId: string;
  inputImage: string;
  roomType:
    | "livingroom"
    | "kitchen"
    | "diningroom"
    | "bedroom"
    | "bathroom"
    | "kidsroom"
    | "familyroom"
    | "readingnook"
    | "sunroom"
    | "walkincloset"
    | "mudroom"
    | "toyroom"
    | "office"
    | "foyer"
    | "powderroom"
    | "laundryroom"
    | "gym"
    | "basement"
    | "garage"
    | "balcony"
    | "cafe"
    | "homebar"
    | "study_room"
    | "front_porch"
    | "back_porch"
    | "back_patio";
  designStyle:
    | "minimalist"
    | "scandinavian"
    | "industrial"
    | "boho"
    | "traditional"
    | "artdeco"
    | "midcenturymodern"
    | "coastal"
    | "tropical"
    | "eclectic"
    | "contemporary"
    | "frenchcountry"
    | "rustic"
    | "shabbychic"
    | "vintage"
    | "country"
    | "modern"
    | "asian_zen"
    | "hollywoodregency"
    | "bauhaus"
    | "mediterranean"
    | "farmhouse"
    | "victorian"
    | "gothic"
    | "moroccan"
    | "southwestern"
    | "transitional"
    | "maximalist"
    | "arabic"
    | "japandi"
    | "retrofuturism"
    | "artnouveau";
  numImages: number;
  keepOriginalDimensions: boolean;
  colorScheme:
    | "COLOR_SCHEME_0"
    | "COLOR_SCHEME_1"
    | "COLOR_SCHEME_2"
    | "COLOR_SCHEME_3"
    | "COLOR_SCHEME_4"
    | "COLOR_SCHEME_5"
    | "COLOR_SCHEME_6"
    | "COLOR_SCHEME_7"
    | "COLOR_SCHEME_8"
    | "COLOR_SCHEME_9"
    | "COLOR_SCHEME_10"
    | "COLOR_SCHEME_11"
    | "COLOR_SCHEME_12"
    | "COLOR_SCHEME_13"
    | "COLOR_SCHEME_14"
    | "COLOR_SCHEME_15"
    | "COLOR_SCHEME_16"
    | "COLOR_SCHEME_17"
    | "COLOR_SCHEME_18"
    | "COLOR_SCHEME_19"
    | "COLOR_SCHEME_20";
  specialityDecor:
    | "SPECIALITY_DECOR_0"
    | "SPECIALITY_DECOR_1"
    | "SPECIALITY_DECOR_2"
    | "SPECIALITY_DECOR_3"
    | "SPECIALITY_DECOR_4"
    | "SPECIALITY_DECOR_5"
    | "SPECIALITY_DECOR_6"
    | "SPECIALITY_DECOR_7";
  scaleFactor: number;
}

export interface GenerateMusicRequest {
  customMode: boolean;
  instrumental: boolean;
  callBackUrl: string;
  prompt?: string;
  style?: string;
  title?: string;
  model?: "V3_5" | "V4";
}

export interface ExtendMusicRequest {
  defaultParamFlag: boolean;
  audioId: string;
  callBackUrl: string;
  prompt?: string;
  style?: string;
  title?: string;
  continueAt?: number;
  model?: "V3_5" | "V4";
}

export interface GenerateWavRequest {
  callBackUrl: string;
  audioId?: string;
  taskId?: string;
}

export interface GenerateMusicResponse {
  id: string;
  status: string;
  audio_url?: string;
  error?: string;
}

export type TaskStatus =
  | "PENDING"
  | "SUCCESS"
  | "CREATE_TASK_FAILED"
  | "GENERATE_AUDIO_FAILED";

export interface RecordInfoResponse {
  msg: string;
  code: number;
  data: {
    musicId: string;
    musicIndex: number;
    createTime: string;
    response: object;
    errorMessage: string;
    completeTime: string;
    errorCode: number;
    callbackUrl: string;
    taskId: string;
    successFlag: TaskStatus;
  };
}

export interface WavGenerationResponse {
  msg: string;
  code: number;
  data: {
    taskId: string;
  };
}

export interface Agent {
  id: string;
  name: string;
  modelName: string;
  temperature: number;
  description: string;
  systemPrompt: string;
  tools: string[];
  createdAt: string;
  updatedAt: string;
  greeting?: string;
  profilePicture?: string;
  userId?: string;
  user?: User;
  usageMode: AgentUsageMode;
}

export interface Voice {
  id: string;
  name: string;
  description: string;
}

export enum AgentUsageMode {
  ANONYMOUS = "ANONYMOUS",
  AUTHENTICATED = "AUTHENTICATED",
}

export interface Character {
  id: string;
  name: string;
  modelName: string;
  temperature: number;
  description: string;
  systemPrompt: string;
  tagline: string;
  greeting: string;
  voiceId: string;
  knowledgeFileId: string;
  public: boolean;
  createdAt: string;
  updatedAt: string;
  userId?: string;
  user?: User;
  voice?: Voice;
  knowledgeFile?: File;
}

export interface SubUser {
  id: string;
  name: string;
  email: string;
  password: string;
  phone?: string;
  createdAt: string;
  updatedAt: string;
  userId: string;
  user: User;
}
